import { useEffect } from 'react'

import { useMetaSink } from './metaSink'
import { LOCALES, type Locale } from './types'

/**
 * Everything that can go in the document head for one page.
 *
 * Search engines are the whole reason this file exists: the site is
 * prerendered to static HTML at build time, and these values are baked into
 * that HTML, so a crawler sees a complete, correct head without running any
 * JavaScript.
 */
export interface PageMeta {
  title: string
  description: string
  /** Path without the language prefix, e.g. "/gallery/kartyny". */
  path: string
  locale: Locale
  /** Absolute URL of the image used in social previews. */
  image?: string
  imageAlt?: string
  type?: 'website' | 'article'
  /** JSON-LD objects. Rich results are built from these. */
  structuredData?: Record<string, unknown>[]
  /** Keeps a page out of search results — used for 404s. */
  noIndex?: boolean
}

const isBrowser = typeof document !== 'undefined'

/**
 * Declares the head for the current page.
 *
 * On the server it records the values so the prerender script can write them
 * into the HTML. In the browser it updates the live document after navigation,
 * because a client-side route change does not reload the page and the tags
 * would otherwise still describe the previous one.
 */
export function useSeo(meta: PageMeta): void {
  const sink = useMetaSink()

  // During server rendering there is no effect phase, so the value is recorded
  // inline. React renders this synchronously and once, so it is safe here.
  if (!isBrowser && sink) {
    sink.set(meta)
  }

  // A single string of everything that can change: the meta object is rebuilt
  // on every render, so comparing it by identity would rewrite the document
  // head on every keystroke in a form.
  const fingerprint = JSON.stringify(meta)

  useEffect(() => {
    if (!isBrowser) return
    applyMeta(meta)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint])
}

export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'http://localhost:5173'

/** Absolute URL of a page in a given language. */
export function canonicalURL(locale: Locale, path: string): string {
  const clean = path === '/' ? '' : path.replace(/\/+$/, '')
  return `${SITE_URL.replace(/\/+$/, '')}/${locale}${clean}`
}

interface Tag {
  tag: 'meta' | 'link' | 'script' | 'title'
  attrs: Record<string, string>
  content?: string
}

/**
 * Turns a PageMeta into the list of head tags.
 *
 * Shared between the prerender script (which serialises them into HTML) and
 * the browser (which applies them to the live document), so the two can never
 * drift apart.
 */
export function metaTags(meta: PageMeta): Tag[] {
  const canonical = canonicalURL(meta.locale, meta.path)
  const tags: Tag[] = [
    { tag: 'title', attrs: {}, content: meta.title },
    { tag: 'meta', attrs: { name: 'description', content: meta.description } },
    { tag: 'link', attrs: { rel: 'canonical', href: canonical } },

    { tag: 'meta', attrs: { property: 'og:type', content: meta.type ?? 'website' } },
    { tag: 'meta', attrs: { property: 'og:title', content: meta.title } },
    { tag: 'meta', attrs: { property: 'og:description', content: meta.description } },
    { tag: 'meta', attrs: { property: 'og:url', content: canonical } },
    {
      tag: 'meta',
      attrs: { property: 'og:locale', content: meta.locale === 'uk' ? 'uk_UA' : 'en_US' },
    },
    {
      tag: 'meta',
      attrs: { name: 'twitter:card', content: meta.image ? 'summary_large_image' : 'summary' },
    },
  ]

  if (meta.image) {
    tags.push({ tag: 'meta', attrs: { property: 'og:image', content: meta.image } })
    tags.push({
      tag: 'meta',
      attrs: { property: 'og:image:alt', content: meta.imageAlt ?? meta.title },
    })
  }

  // hreflang tells Google that the Ukrainian and English pages are the same
  // content in two languages rather than duplicates competing with each other.
  for (const locale of LOCALES) {
    tags.push({
      tag: 'link',
      attrs: { rel: 'alternate', hreflang: locale, href: canonicalURL(locale, meta.path) },
    })
  }
  tags.push({
    tag: 'link',
    attrs: { rel: 'alternate', hreflang: 'x-default', href: canonicalURL('uk', meta.path) },
  })

  if (meta.noIndex) {
    tags.push({ tag: 'meta', attrs: { name: 'robots', content: 'noindex, follow' } })
  }

  for (const data of meta.structuredData ?? []) {
    tags.push({
      tag: 'script',
      attrs: { type: 'application/ld+json' },
      content: JSON.stringify(data),
    })
  }

  return tags
}

/** Serialises head tags to HTML. Used by the prerender script. */
export function renderHeadTags(meta: PageMeta): string {
  return metaTags(meta)
    .map((tag) => {
      const attrs = Object.entries(tag.attrs)
        .map(([key, value]) => `${key}="${escapeAttribute(value)}"`)
        .join(' ')

      if (tag.tag === 'title') {
        return `<title>${escapeText(tag.content ?? '')}</title>`
      }
      if (tag.tag === 'script') {
        // JSON-LD is embedded verbatim; only "<" needs neutralising so the
        // browser cannot be tricked into ending the script tag early.
        return `<script ${attrs}>${(tag.content ?? '').replace(/</g, '\\u003c')}</script>`
      }
      if (tag.tag === 'link') {
        return `<link ${attrs}>`
      }
      return `<meta ${attrs}>`
    })
    .join('\n    ')
}

/** Applies head tags to the live document after a client-side navigation. */
function applyMeta(meta: PageMeta): void {
  document.title = meta.title
  document.documentElement.lang = meta.locale

  // Remove what a previous page put there, leaving the static tags alone.
  document.head.querySelectorAll('[data-seo]').forEach((node) => node.remove())

  for (const tag of metaTags(meta)) {
    if (tag.tag === 'title') continue

    const element = document.createElement(tag.tag)
    for (const [key, value] of Object.entries(tag.attrs)) {
      element.setAttribute(key, value)
    }
    if (tag.content !== undefined) element.textContent = tag.content
    element.setAttribute('data-seo', '')
    document.head.appendChild(element)
  }
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/* -------------------------------------------------------------------------
   Structured data builders
   ------------------------------------------------------------------------- */

export function personSchema(name: string, description: string, image?: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    description,
    jobTitle: 'Artist',
    url: SITE_URL,
    ...(image ? { image } : {}),
  }
}

export function artworkSchema(input: {
  name: string
  description: string
  image?: string
  dateCreated?: string
  url: string
  artistName: string
  category: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VisualArtwork',
    name: input.name,
    description: input.description,
    url: input.url,
    artform: input.category,
    creator: { '@type': 'Person', name: input.artistName },
    ...(input.image ? { image: input.image } : {}),
    ...(input.dateCreated ? { dateCreated: input.dateCreated } : {}),
  }
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}
